import React, { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { convert, FX_SUPPORTED } from "@/lib/fx";
import { getCurrency } from "@/lib/currencies";

/**
 * CurrencyAmountInput
 * -------------------
 * Numeric cost/revenue input with an inline currency selector.
 * The user can enter Mining in USD, Processing in INR — everything auto-converts
 * to the project's base currency on submit.
 *
 * Value stored (via `onChange`) is ALWAYS in project (base) currency, so no
 * backend changes are required.
 *
 * Props:
 *   - value                    number (project-currency)
 *   - onChange(nextBaseValue)  fires with number in base currency
 *   - label                    field label (e.g. "Mining cost /t")
 *   - baseCurrency             project currency ISO (e.g. "USD", "INR")
 *   - inputCurrency            currency the user is typing in
 *   - onInputCurrencyChange    fires with new input currency ISO
 *   - testId                   data-testid prefix
 *   - step, min                number input attrs
 */
export default function CurrencyAmountInput({
  value,
  onChange,
  label,
  baseCurrency = "USD",
  inputCurrency,
  onInputCurrencyChange,
  testId = "cost",
  step = 0.01,
  min,
  hint,
}) {
  const activeCur = inputCurrency || baseCurrency;
  const activeSym = getCurrency(activeCur).symbol || activeCur;
  const baseSym = getCurrency(baseCurrency).symbol || baseCurrency;

  // Display: convert the stored base-currency value back into the input currency
  const displayValue = useMemo(
    () => convert(value ?? 0, baseCurrency, activeCur),
    [value, baseCurrency, activeCur]
  );

  const handleChange = (e) => {
    const raw = e.target.value;
    const parsed = raw === "" ? 0 : parseFloat(raw);
    // Convert user's input → base currency → store
    const inBase = convert(parsed, activeCur, baseCurrency);
    onChange?.(inBase);
  };

  const showFxHint = activeCur !== baseCurrency && Number.isFinite(value) && value !== 0;

  return (
    <div className="min-w-0">
      <Label className="smin-label">{label}</Label>
      <div className="mt-1 flex items-stretch gap-1">
        <div className="flex-1 flex items-center border border-border bg-input pl-2 min-w-0">
          <span className="text-muted-foreground font-mono text-xs mr-1 shrink-0">
            {activeSym}
          </span>
          <Input
            type="number"
            step={step}
            min={min}
            value={Number.isFinite(displayValue) ? displayValue : 0}
            onChange={handleChange}
            data-testid={`${testId}-amount`}
            className="border-0 bg-transparent font-mono px-0 h-9 focus-visible:ring-0 focus-visible:ring-offset-0 min-w-0"
          />
        </div>
        <select
          value={activeCur}
          onChange={(e) => onInputCurrencyChange?.(e.target.value)}
          data-testid={`${testId}-currency`}
          className="w-16 h-9 border border-border bg-input font-mono text-[11px] uppercase tracking-wider px-1.5 focus:outline-none focus:border-primary transition-colors cursor-pointer"
        >
          {FX_SUPPORTED.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      {showFxHint && (
        <div className="mt-1 font-mono text-[10px] text-muted-foreground truncate">
          ≈ <span className="text-foreground">{baseSym}{value.toLocaleString("en-US", { maximumFractionDigits: 2 })}</span> in {baseCurrency}
        </div>
      )}
      {hint && !showFxHint && (
        <div className="mt-1 font-mono text-[10px] text-muted-foreground truncate">{hint}</div>
      )}
    </div>
  );
}
